import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Search, Mail, ExternalLink } from "lucide-react";

const STATUS_TABS = ["All", "In Progress", "Reward unlocked", "Reward redeemed"];
const AWAY_TABS = [null, 1, 2, 3];

const REWARD_LABEL = {
  half_off_6th: "6th Treatment — 50% OFF",
  free_10th:    "10th Treatment — FREE",
};

/** CLAÈ Loyalty — admin list with filters, per-client detail, resend welcome. */
export default function AdminLoyalty() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [away, setAway] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "All") params.status = status;
      if (away != null) params.away = away;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/loyalty", { params });
      setRows(r.data.rows || []);
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load loyalty data");
    }
    setLoading(false);
  }, [status, away, q]);

  // Auto-reload on status/away filter changes. ``q`` is captured by the
  // callback but intentionally does NOT trigger reload — search submits
  // on Enter via the input handler.
  useEffect(() => { load(); }, [status, away, load]);

  const resendWelcome = async (clientId) => {
    try {
      const r = await api.post(`/admin/loyalty/by-client/${clientId}/send-welcome`);
      toast.success(`Loyalty welcome email sent to ${r.data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-loyalty">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="mono-label">Loyalty</p>
          <h2 className="font-serif text-3xl text-stone-900">CLAÈ Loyalty Card</h2>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="loyalty-summary">
          <SummaryTile label="ACTIVE CLIENTS"    value={summary.total} testid="tile-total" />
          <SummaryTile label="REWARDS UNLOCKED"  value={summary.rewards_unlocked} testid="tile-unlocked" />
          <SummaryTile label="REWARDS REDEEMED"  value={summary.rewards_redeemed} testid="tile-redeemed" />
          <SummaryTile label="1 TREATMENT AWAY"  value={summary.one_away} testid="tile-one-away" />
          <SummaryTile label="2 TREATMENTS AWAY" value={summary.two_away} testid="tile-two-away" />
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center border hairline bg-white px-3 py-2 flex-1 min-w-[260px]">
          <Search size={14} className="text-stone-500 mr-2" />
          <input
            data-testid="loyalty-search"
            placeholder="Search name, email, or client ID"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="flex-1 text-sm focus:outline-none"
          />
        </div>
        <select
          data-testid="loyalty-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border hairline bg-white px-3 py-2 text-sm"
        >
          {STATUS_TABS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="loyalty-away-filter"
          value={away ?? ""}
          onChange={(e) => setAway(e.target.value === "" ? null : Number(e.target.value))}
          className="border hairline bg-white px-3 py-2 text-sm"
        >
          <option value="">Any distance</option>
          {AWAY_TABS.filter(Boolean).map((n) => <option key={n} value={n}>{n} treatment{n > 1 ? "s" : ""} away</option>)}
        </select>
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Progress</th>
              <th className="text-left px-4 py-3">Next reward</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-6 text-stone-500 text-center">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-stone-500 text-center" data-testid="loyalty-empty">No loyalty records yet. Cards are created after a client's first completed treatment.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.card_id} className="border-t hairline hover:bg-stone-50" data-testid={`loyalty-row-${r.client_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.client_name || "—"}</div>
                  <div className="text-xs text-stone-500">{r.client_email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {r.progress} / {r.progress < 5 ? 5 : 9}
                </td>
                <td className="px-4 py-3">{r.next_reward}</td>
                <td className="px-4 py-3">
                  {r.status === "Reward unlocked" ? (
                    <span className="mono-label gold" data-testid={`loyalty-status-${r.client_id}`}>REWARD UNLOCKED</span>
                  ) : (
                    <span className="mono-label text-stone-600" data-testid={`loyalty-status-${r.client_id}`}>
                      {r.treatments_away} away
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`loyalty-view-${r.client_id}`}
                    onClick={() => setSelected(r)}
                    className="mono-label text-stone-600 hover:text-stone-900 mr-3"
                  >
                    View
                  </button>
                  <a
                    href={`/loyalty/${r.view_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mono-label gold inline-flex items-center gap-1"
                    data-testid={`loyalty-open-${r.client_id}`}
                  >
                    Open card <ExternalLink size={11} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <LoyaltyDetail
          row={selected}
          onClose={() => setSelected(null)}
          onResend={() => resendWelcome(selected.client_id)}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="border hairline bg-white px-4 py-3" data-testid={testid}>
      <p className="mono-label text-stone-500 text-[10px]">{label}</p>
      <p className="font-serif text-2xl mt-1">{value}</p>
    </div>
  );
}

function LoyaltyDetail({ row, onClose, onResend }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/loyalty/by-client/${row.client_id}`).then((r) => setData(r.data)).catch(() => {});
  }, [row.client_id]);

  const rewards = useMemo(() => {
    if (!data) return [];
    const flat = [];
    for (const c of [data.current_cycle, ...(data.past_cycles || [])]) {
      for (const r of (c.rewards || [])) {
        flat.push({ ...r, cycle_index: c.cycle_index });
      }
    }
    return flat;
  }, [data]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loyalty-detail-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border hairline">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <p className="mono-label">Loyalty · {row.client_name}</p>
            <p className="text-xs text-stone-500">{row.client_email} · {row.client_id}</p>
          </div>
          <button data-testid="loyalty-detail-close" onClick={onClose} className="mono-label text-stone-500 hover:text-stone-900">✕</button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <Metric label="CYCLE" value={row.cycle_index} />
            <Metric label="PROGRESS" value={`${row.progress} / ${row.progress < 5 ? 5 : 9}`} />
            <Metric label="STATUS" value={row.status} />
          </div>

          <div>
            <p className="mono-label mb-2">Reward history</p>
            {rewards.length === 0 && <p className="text-sm text-stone-500">No rewards unlocked yet.</p>}
            {rewards.map((r, i) => (
              <div key={i} className="border-t hairline py-3 flex items-baseline justify-between" data-testid={`loyalty-reward-${r.kind}-${r.cycle_index}`}>
                <div>
                  <p className="text-sm">{REWARD_LABEL[r.kind] || r.label}</p>
                  <p className="text-xs text-stone-500">Cycle {r.cycle_index}</p>
                </div>
                <div className="text-right text-xs">
                  <p className={r.redeemed_at ? "mono-label text-stone-400" : "mono-label gold"}>
                    {r.redeemed_at ? "REDEEMED" : "UNLOCKED"}
                  </p>
                  {r.unlocked_at && <p className="text-stone-500">Unlocked {new Date(r.unlocked_at).toLocaleDateString("en-GB")}</p>}
                  {r.redeemed_at && <p className="text-stone-500">Redeemed {new Date(r.redeemed_at).toLocaleDateString("en-GB")}</p>}
                  {r.redeemed_booking_reference && <p className="font-mono text-stone-500">{r.redeemed_booking_reference}</p>}
                </div>
              </div>
            ))}
          </div>

          {data?.current_cycle?.stamps?.length > 0 && (
            <div>
              <p className="mono-label mb-2">Treatments this cycle</p>
              {data.current_cycle.stamps.map((s, i) => (
                <div key={i} className="border-t hairline py-2 flex justify-between text-sm">
                  <span>{s.treatment_name}</span>
                  <span className="text-stone-500">{s.appointment_date}</span>
                </div>
              ))}
            </div>
          )}

          <div className="pt-4 border-t hairline flex items-center justify-between">
            <p className="text-xs text-stone-500">
              Welcome email {row.welcome_email_sent_at ? `sent ${new Date(row.welcome_email_sent_at).toLocaleDateString("en-GB")}` : "not sent"}
            </p>
            <button
              data-testid="loyalty-resend-welcome"
              onClick={onResend}
              className="mono-label inline-flex items-center gap-2 border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white transition"
            >
              <Mail size={12} /> {row.welcome_email_sent_at ? "Resend welcome email" : "Send welcome email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border hairline bg-white px-3 py-2">
      <p className="mono-label text-stone-500 text-[10px]">{label}</p>
      <p className="font-serif text-lg mt-0.5">{value}</p>
    </div>
  );
}
